import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { hpe } from 'grommet-theme-hpe';
import FocusTrap from 'focus-trap-react';

import {
  Box,
  Button,
  Footer,
  Grommet,
  Heading,
  Layer,
  Paragraph,
  Spinner,
} from 'grommet';

import { Head } from '@components/basicComponents';
import { FormUrlParam } from '@customTypes/enums';
import type { ModalInfo } from '@customTypes/types';
import { MODAL_INFO_DATA } from '@constants/modalInfo';
import './App.css';

import {
  BlankRequestGLCPForm,
  ComputeOpsManagementSupportForm,
  ConsolidatedGLCPForm,
  ConsolidatedGLCPFormTest,
  DSCCForm,
  DeveloperPortalSupportForm,
  GlobalTradeSupportForm,
  GlobalTradeSupportCenterForm,
  GreenLakeCentralSupportForm,
  GreenLakeSupportGLCPForm,
  LoginSupportForm,
  LoginSupportGLCPForm,
  LoginSupportMLForm,
  MarketplaceGLCPForm,
  OpsRampForm,
  ManagedCloudServiceForm,
  NetworkingArubaCentralForm,
  DashboardsAndMonitoringForm,
  SupportHubForm,
  OSQBMSupportForm,
  PrivateCloudSupportForm,
  ProjectDenaliSupportForm,
  RPLSupportForm,
  RPLSupportGLCPForm,
  SoftwareDownloadRequestForm,
  SWDRequestGLCPForm,
} from './components';

export const App = () => {
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchUrlParam, setSearchUrlParam] = useState<string>();
  const [modalInfo, setModalInfo] = useState<ModalInfo>();

  const location = useLocation();

  useEffect(() => {
    let formParamValue = new URLSearchParams(location.search).get('form');

    switch (formParamValue) {
      case FormUrlParam.GlobalTrade_old:
        window.location.replace(location.pathname + '?form=' + FormUrlParam.GlobalTrade);
        break;
      case FormUrlParam.GlobalTrade_SupportCenter_old:
        window.location.replace(location.pathname + '?form=' + FormUrlParam.GlobalTrade_SupportCenter);
        break;
      case FormUrlParam.RPL_SupportCenter_old:
        window.location.replace(location.pathname + '?form=' + FormUrlParam.RPL_SupportCenter);
        break;
      case FormUrlParam.RPL_SupportCenter_GLCP_old:
        window.location.replace(location.pathname + '?form=' + FormUrlParam.RPL_SupportCenter_GLCP);
        break;
    }

    formParamValue && setSearchUrlParam(formParamValue);
  }, [location, searchUrlParam]);

  const successResponse = (state: boolean, modalInfo?: ModalInfo): void => {
    setSuccess(state);
    setModalInfo(modalInfo || MODAL_INFO_DATA.success);
  };

  const errorResponse = (state: boolean): void => {
    setError(state);
    setModalInfo(MODAL_INFO_DATA.error);
  };

  const loadingResponse = (state: boolean): void => {
    setLoading(state);
  };

  const formResponses = {
    onSuccess: successResponse,
    onError: errorResponse,
    onLoading: loadingResponse,
  };

  const returnFunction = (): void => {
    setSuccess(false);
    setError(false);
    resetForm();
  };

  const resetForm = (): void => {
    (document.querySelector('.App form') as HTMLFormElement)?.reset();
  };

  const renderForm = (searchUrlParam: string): JSX.Element | undefined => {
    switch (searchUrlParam) {
      case FormUrlParam.BlankRequest:
        return <BlankRequestGLCPForm {...formResponses} />;
      case FormUrlParam.ComputeSupport:
        return <ComputeOpsManagementSupportForm {...formResponses} />;
      case FormUrlParam.Denali:
        return (process.env.REACT_APP_SHOW_DENALI_FORM === 'true') ? <ProjectDenaliSupportForm {...formResponses} /> : <></>;
      case FormUrlParam.DeveloperPortal:
        return <DeveloperPortalSupportForm {...formResponses} />;
      case FormUrlParam.DSSupport:
        return <DSCCForm {...formResponses} />;
      case FormUrlParam.GLCentral:
        return <GreenLakeCentralSupportForm {...formResponses} />;
      case FormUrlParam.GLSupport:
        return <GreenLakeSupportGLCPForm {...formResponses} />;
      case FormUrlParam.GlobalTrade:
      case FormUrlParam.GlobalTrade_old:
        return <GlobalTradeSupportForm {...formResponses} />;
      case FormUrlParam.GlobalTrade_SupportCenter:
      case FormUrlParam.GlobalTrade_SupportCenter_old:
        return <GlobalTradeSupportCenterForm {...formResponses} />;
      case FormUrlParam.GreenLakeSupport:
        return <ConsolidatedGLCPForm {...formResponses} />;
      case FormUrlParam.GreenLakeSupportTest:
        return (process.env.REACT_APP_SHOW_CONSOLIDATED_TEST_FORM === 'true') ? <ConsolidatedGLCPFormTest {...formResponses} /> : <></>;
      case FormUrlParam.LoginSupport:
        return <LoginSupportForm {...formResponses} />;
      case FormUrlParam.LoginSupport_GLCP:
        return <LoginSupportGLCPForm {...formResponses} />;
      case FormUrlParam.LoginSupport_ML:
        return <LoginSupportMLForm {...formResponses} />;
      case FormUrlParam.Marketplace:
        return <MarketplaceGLCPForm {...formResponses} />;
      case FormUrlParam.OpsRamp:
        return <OpsRampForm {...formResponses} />
      case FormUrlParam.ManagedCloudService:
        return <ManagedCloudServiceForm {...formResponses} />
      case FormUrlParam.NetworkingArubaCentral:
        return <NetworkingArubaCentralForm {...formResponses} />  
      case FormUrlParam.DashboardsAndMonitoring:
        return <DashboardsAndMonitoringForm {...formResponses} />
      case FormUrlParam.SupportHub:
        return <SupportHubForm {...formResponses} />;
      case FormUrlParam.OSQBM:
        return <OSQBMSupportForm {...formResponses} />;
      case FormUrlParam.PrivateCloud:
        return <PrivateCloudSupportForm {...formResponses} />;
      case FormUrlParam.RPL_SupportCenter:
      case FormUrlParam.RPL_SupportCenter_old:
        return <RPLSupportForm {...formResponses} />;
      case FormUrlParam.RPL_SupportCenter_GLCP:
      case FormUrlParam.RPL_SupportCenter_GLCP_old:
        return <RPLSupportGLCPForm {...formResponses} />;
      case FormUrlParam.SWD_SupportCenter:
        return <SoftwareDownloadRequestForm {...formResponses} />;
      case FormUrlParam.SWD_SupportCenter_GLCP:
        return <SWDRequestGLCPForm {...formResponses} />;
    }
  };

  return (
    <div className="App">
      <HelmetProvider>
        <Head searchUrlParam={searchUrlParam}></Head>
        <Grommet theme={hpe}>
          <Box margin={{ top: 'large', bottom: 'xlarge' }}>
            {searchUrlParam && renderForm(searchUrlParam)}

            {(success || error) && modalInfo &&
              <Layer
                animation={false}
                onClickOutside={returnFunction}
                onEsc={returnFunction}
              >
                <FocusTrap focusTrapOptions={{ initialFocus: 'button' }}>
                  <Box gap="medium" pad="medium">
                    <Heading level={2} margin="none">
                      {modalInfo.statusText}
                    </Heading>
                    <Paragraph margin="none">
                      {modalInfo.statusMessage}
                    </Paragraph>
                    {modalInfo.extraData &&
                      <Paragraph margin="none">{modalInfo.extraData}</Paragraph>
                    }
                    <Footer justify="end">
                      <Button
                        label={modalInfo.buttonLabel}
                        primary
                        onClick={returnFunction}
                        a11yTitle={modalInfo.buttonA11yTitle}
                      />
                    </Footer>
                  </Box>
                </FocusTrap>
              </Layer>
            }

            {loading &&
              <Layer
                full
                animation={false}
                background="transparent"
              >
                <Box flex="grow" justify="center" align="center">
                  <Spinner size="medium" />
                </Box>
              </Layer>
            }
          </Box>
        </Grommet>
      </HelmetProvider>
    </div>
  );
};
